<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import ArgonAlert from "@/components/ArgonAlert.vue";
import { ROLES } from "@/services/acl";
import UserActivityLog from "@/components/UserActivityLog.vue";
import MediaComplianceRuleWidget from "@/components/MediaComplianceRuleWidget.vue";
import {
  deleteUser,
  updateUserStatus,
  fetchUserById,
  fetchUserPurchases,
  fetchUserPlanUsage,
  setUserPlanFeatureUsage,
  fetchUserWallet,
  addWalletTransaction,
  fetchRefreshUserPurchases,
  updateUserPurchasePeriod,
  transferUserPlan,
  fetchVerificationAttempts,
  fetchVerificationMedia,
  updateVerificationAttemptStatus,
  fetchUserMedia,
  fetchUserMatchQuery,
  fetchUserSessionToken,
  fetchUserSessions,
  fetchFreeTextTranslation,
  getMediaUrl,
  updateUser,
  deleteMediaLabel,
  pushUserClientEvents,
  markAtomInappropriateContent,
  unmarkAtomInappropriateContent,
} from "@/services/backofficeClient";

const TRANSLATION_LANGUAGES = [
  { code: "English", label: "English" },
  { code: "Persian", label: "Persian" },
];

const route = useRoute();
const router = useRouter();
const store = useStore();
const canEditUser = computed(() => store.getters.hasRole(ROLES.USER_EDIT));
const canDeleteMedia = computed(() => store.getters.hasRole(ROLES.MEDIA_DELETE));
const canEditVerification = computed(() =>
  store.getters.hasRole(ROLES.VERIFICATION_EDIT)
);
const canEditPay = computed(() => store.getters.hasRole(ROLES.PAY_EDIT));

const user = ref(null);
const draft = reactive({
  name: "",
  status: "",
  gender: "",
  verificationStatus: "",
  matchScoreMultiplier: "",
  birthday: "",
});
const editing = reactive({
  name: false,
  status: false,
  gender: false,
  verificationStatus: false,
  matchScore: false,
  birthday: false,
});
const saving = reactive({});
const isLoading = ref(true);
const errorMessage = ref("");
const activeTab = ref("profile");
const statusMessage = ref("");
const statusType = ref("success");
const statusReason = ref("");
const deleteReason = ref("");
const activityLogRef = ref(null);
const showDeleteModal = ref(false);
const deleteConfirmation = ref("");
const deleteError = ref("");
const isDeleting = ref(false);
const isPushingClientEvents = ref(false);
const showMediaModal = ref(false);
const currentMediaUrl = ref("");
const currentMediaObjectUrl = ref("");
const verificationAttempts = ref([]);
const isVerificationLoading = ref(false);
const verificationErrorMessage = ref("");
const attemptNextStatus = reactive({});
const updatingAttempt = reactive({});
const sessionJwtLoading = reactive({});
const purchases = ref([]);
const purchasesPagination = reactive({
  page: 1,
  pageSize: 25,
  totalItems: 0,
  totalPages: 0,
  hasNext: false,
});
const purchasesLoading = ref(false);
const purchasesError = ref("");
const planUsage = ref([]);
const planUsageLoading = ref(false);
const planUsageError = ref("");
const wallet = ref(null);
const walletLoading = ref(false);
const walletError = ref("");
const showAddTransactionModal = ref(false);
const newTransaction = reactive({
  type: "credit",
  amount: "",
  description: "",
});
const addTransactionError = ref("");
const isAddingTransaction = ref(false);
const showEditPurchasePeriodModal = ref(false);
const editingPurchase = reactive({
  id: "",
  fromDatetime: "",
  toDatetime: "",
});
const editPurchasePeriodError = ref("");
const isSavingPurchasePeriod = ref(false);
const showEditUsageModal = ref(false);
const editingUsage = reactive({
  planId: "",
  planName: "",
  payableFeature: "",
  usageCount: "",
});
const editUsageError = ref("");
const isSavingUsage = ref(false);
const showTransferPlanModal = ref(false);
const transferringPurchase = reactive({ id: "", planId: "" });
const transferDestinationUserId = ref("");
const transferResolvedUser = ref(null);
const transferLookupError = ref("");
const isLookingUpTransferUser = ref(false);
const transferPlanError = ref("");
const isTransferringPlan = ref(false);
const mediaItems = ref([]);
const mediaPagination = reactive({
  page: 1,
  pageSize: 25,
  totalItems: 0,
  totalPages: 0,
  hasNext: false,
});
const mediaLoading = ref(false);
const mediaError = ref("");
const sessions = ref([]);
const sessionsLoading = ref(false);
const sessionsError = ref("");
const matchQuery = ref("");
const matchQueryError = ref("");
const matchQueryStatus = ref("");
const matchQueryFetched = ref(false);
const isMatchQueryLoading = ref(false);
let matchQueryStatusTimeout = null;

const STATUS_OPTIONS = ["active", "ghosted", "blocked"];
const GENDER_OPTIONS = ["female", "male", "nonBinary"];
const VERIFICATION_STATUS_OPTIONS = [
  "waiting_for_review",
  "verified",
  "rejected_code",
  "rejected_selfie",
  "required",
];

const tabs = [
  { key: "profile", label: "Profile Details" },
  { key: "attributes", label: "Profile Attributes" },
  { key: "media", label: "Media" },
  { key: "verification", label: "Verification" },
  { key: "sessions", label: "Sessions" },
  { key: "purchases", label: "Purchases" },
];
const tabKeys = tabs.map((tab) => tab.key);

const photoAtoms = computed(() => {
  const atoms = Array.isArray(user.value?.atoms) ? user.value.atoms : [];
  return atoms.filter((atom) => atom?.attribute === "photo");
});

const inappropriateContentLoadingFor = ref(null);

const isAtomFlaggedInappropriate = (atomId) => {
  if (!atomId) return false;
  const reasons = user.value?.invalidityReasons || [];
  return reasons.some(
    (reason) =>
      reason?.type === "inappropriateContent" &&
      reason?.atomReference?.id === atomId
  );
};

const toggleAtomInappropriateContent = async (atom) => {
  if (!atom?.id || !user.value?.id || inappropriateContentLoadingFor.value) {
    return;
  }
  inappropriateContentLoadingFor.value = atom.id;
  statusMessage.value = "";
  const wasFlagged = isAtomFlaggedInappropriate(atom.id);
  try {
    const payload = wasFlagged
      ? await unmarkAtomInappropriateContent(user.value.id, atom.id)
      : await markAtomInappropriateContent(user.value.id, atom.id);
    user.value = normalizeUser(payload);
    statusMessage.value = wasFlagged
      ? "Content unmarked as inappropriate."
      : "Content marked as inappropriate.";
    statusType.value = "success";
  } catch (error) {
    console.error("Failed to update inappropriate content flag", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to update inappropriate content flag.";
    statusType.value = "danger";
  } finally {
    inappropriateContentLoadingFor.value = null;
  }
};

const firstPhotoUrl = computed(() => {
  const first = photoAtoms.value[0];
  return first ? getMediaUrl(first.mediaPath) : null;
});

const handleMediaComplianceStatus = ({ message, type }) => {
  statusMessage.value = message;
  statusType.value = type;
};

const groupedAtoms = computed(() => {
  const atoms = Array.isArray(user.value?.atoms) ? user.value.atoms : [];
  const groups = {};
  atoms.forEach((atom) => {
    const key = atom?.attribute || "unknown";
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(atom);
  });
  return Object.entries(groups)
    .map(([attribute, atomsForAttr]) => ({
      attribute,
      atoms: atomsForAttr.sort(
        (a, b) => (a?.order || 0) - (b?.order || 0)
      ),
    }))
    .sort((a, b) => a.attribute.localeCompare(b.attribute));
});

const textAtoms = computed(() => {
  const result = [];
  groupedAtoms.value.forEach((group) => {
    group.atoms.forEach((atom) => {
      if (
        atom?.id &&
        !atom.mediaPath &&
        typeof atom.textValue === "string" &&
        atom.textValue.trim()
      ) {
        result.push(atom);
      }
    });
  });
  return result;
});

const translateMenuOpen = ref(false);
const translateLoading = ref(false);
const translateError = ref("");
const activeTranslationLanguage = ref("");
const translationsByLanguage = reactive({}); // language -> { atomId: translatedText }

const toggleTranslateMenu = () => {
  translateMenuOpen.value = !translateMenuOpen.value;
};

const translationFor = (atom) => {
  if (!activeTranslationLanguage.value || !atom?.id) return null;
  const cache = translationsByLanguage[activeTranslationLanguage.value];
  return cache ? cache[atom.id] || null : null;
};

const translateAttributes = async (language) => {
  translateMenuOpen.value = false;
  translateError.value = "";
  activeTranslationLanguage.value = language;

  const cache =
    translationsByLanguage[language] || (translationsByLanguage[language] = {});
  const pendingAtoms = textAtoms.value.filter((atom) => !(atom.id in cache));
  if (pendingAtoms.length === 0) return;

  translateLoading.value = true;
  try {
    const payload = await fetchFreeTextTranslation(
      pendingAtoms.map((atom) => atom.textValue),
      language
    );
    const translations = Array.isArray(payload?.translations)
      ? payload.translations
      : [];
    pendingAtoms.forEach((atom, index) => {
      const translatedText = translations[index]?.translatedText;
      if (translatedText) {
        cache[atom.id] = translatedText;
      }
    });
  } catch (error) {
    console.error("Failed to translate attributes", error);
    translateError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to translate attributes.";
  } finally {
    translateLoading.value = false;
  }
};

const hasPurchases = computed(() => purchases.value.length > 0);

const platforms = computed(() =>
  Array.from(new Set(deriveDistinct(sessions.value, "platform"))).sort()
);
const appVersions = computed(() =>
  Array.from(new Set(deriveDistinct(sessions.value, "appVersion"))).sort()
);
const languages = computed(() =>
  Array.from(
    new Set(
      deriveDistinct(sessions.value, "language").map((lang) =>
        String(lang).toUpperCase()
      )
    )
  ).sort()
);

const revenueCatUrl = computed(() => {
  return user.value?.id
    ? `https://app.revenuecat.com/projects/3709e151/customers/${user.value.id}`
    : null;
});

const walletTopUpUrl = computed(() => {
  return user.value?.id
    ? `https://wallet.azizamapp.com/?userId=${user.value.id}`
    : null;
});

const ageLabel = computed(() => {
  if (!user.value) return "--";
  const age = Number(user.value.age);
  const birthdayText = user.value.birthday
    ? formatDateOnly(user.value.birthday)
    : null;
  if (!Number.isFinite(age) && !birthdayText) return "--";
  if (Number.isFinite(age) && birthdayText) return `${age} (${birthdayText})`;
  if (Number.isFinite(age)) return age;
  return birthdayText || "--";
});

const locationLabel = computed(() => {
  if (!user.value) return "--";
  if (user.value.locationLabel) return user.value.locationLabel;
  const address = user.value.address || {};
  const city =
    typeof address.city === "object" ? address.city?.en : address.city;
  const country =
    typeof address.country === "object"
      ? address.country?.en
      : address.country;
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (country) return country;
  return "--";
});

const registrationMetadataEntries = computed(() => {
  const metadata = user.value?.registrationMetadata;
  if (!metadata || typeof metadata !== "object") return [];
  return Object.entries(metadata)
    .map(([key, value]) => {
      const isObject = value && typeof value === "object";
      const isEmpty =
        value === null || value === undefined || value === "";
      return {
        key,
        display: isEmpty
          ? "--"
          : isObject
            ? JSON.stringify(value, null, 2)
            : String(value),
        isMultiline: isObject,
      };
    })
    .sort((a, b) => a.key.localeCompare(b.key));
});

const formatDateTime = (value, includeTime = true) => {
  const date = parseDate(value);
  if (!date) return "--";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  if (!includeTime) return `${year}-${month}-${day}`;
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const formatDateOnly = (value) => formatDateTime(value, false);

const formatList = (list, separator = ", ") => {
  if (!Array.isArray(list) || list.length === 0) return "--";
  return list.join(separator);
};

const formatBoolean = (value) => {
  if (value === null || value === undefined) return "--";
  return value ? "Yes" : "No";
};

const formatAgeRange = (minAge, maxAge) => {
  const hasMin = Number.isFinite(minAge);
  const hasMax = Number.isFinite(maxAge);
  if (hasMin && hasMax) return `${minAge} - ${maxAge}`;
  if (hasMin) return `${minAge}+`;
  if (hasMax) return `Up to ${maxAge}`;
  return "--";
};

const formatDistanceKm = (value) => {
  if (!Number.isFinite(value)) return "--";
  return `${value} km`;
};

const formatVerificationAttemptStatus = (status) => {
  if (!status) return "--";
  const spaced = status.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const normalizeTabKey = (value) => {
  if (typeof value !== "string") return tabs[0].key;
  return tabKeys.includes(value) ? value : tabs[0].key;
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseVerificationAttempt = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const nextOptions = Array.isArray(raw.adminNextOptions)
    ? raw.adminNextOptions
      .map((s) => (s == null ? "" : String(s)))
      .filter(Boolean)
    : [];
  return {
    id: raw.id ? String(raw.id) : "",
    status: raw.status || "",
    verificationCode: raw.verificationCode || "",
    verificationGesture: raw.verificationGesture || "",
    insights: raw.insights || "",
    verificationMediaPaths: Array.isArray(raw.verificationMediaPaths)
      ? raw.verificationMediaPaths
        .map((path) => (path == null ? "" : String(path)))
        .filter(Boolean)
      : [],
    verificationPhotoPaths: Array.isArray(raw.verificationPhotoPaths)
      ? raw.verificationPhotoPaths
        .map((path) => (path == null ? "" : String(path)))
        .filter(Boolean)
      : [],
    createdAt: parseDate(raw.createdAt),
    updatedAt: parseDate(raw.updatedAt),
    expiresAt: parseDate(raw.expiresAt),
    adminNextOptions: nextOptions,
  };
};

const normalizePurchase = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const parseDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  return {
    id: raw.id ? String(raw.id) : "",
    userId: raw.userId ? String(raw.userId) : "",
    planId: raw.planId || "",
    acquisitionType: raw.acquisitionType || "",
    purchaseId: raw.purchaseId || "",
    status: raw.status || "",
    environment: raw.environment || "",
    store: raw.store || "",
    autoRenewalStatus: raw.autoRenewalStatus || "",
    fromDatetime: parseDate(raw.fromDatetime),
    toDatetime: parseDate(raw.toDatetime),
    country: raw.country || "",
    revenueCurrency: raw.revenueCurrency || "",
    revenueGross: raw.revenueGross ?? null,
    revenueCommission: raw.revenueCommission ?? null,
    revenueTax: raw.revenueTax ?? null,
    revenueProceeds: raw.revenueProceeds ?? null,
    acquiredAt: parseDate(raw.acquiredAt),
    createdAt: parseDate(raw.createdAt),
    updatedAt: parseDate(raw.updatedAt),
    features: Array.isArray(raw.features)
      ? raw.features.map((feature) => ({
        id: feature?.id ? String(feature.id) : "",
        payableFeature: feature?.payableFeature || "",
        windowLimit: feature?.windowLimit ?? 0,
        windowDuration: feature?.windowDuration || "",
        lastUsageCount: feature?.lastUsageCount ?? 0,
        lastUsedAt: parseDate(feature?.lastUsedAt),
      }))
      : [],
  };
};

const loadPurchases = async (page = 1) => {
  if (!user.value?.id) return;
  purchasesLoading.value = true;
  purchasesError.value = "";
  try {
    const payload = await fetchUserPurchases({
      userId: user.value.id,
      page,
      pageSize: purchasesPagination.pageSize,
    });
    const items = Array.isArray(payload?.items) ? payload.items : [];
    purchases.value = items
      .map((item) => normalizePurchase(item))
      .filter(Boolean);
    const pagination = payload?.pagination || {};
    purchasesPagination.page = pagination.page || page;
    purchasesPagination.pageSize = pagination.pageSize || purchasesPagination.pageSize;
    purchasesPagination.totalItems = pagination.totalItems || 0;
    purchasesPagination.totalPages = pagination.totalPages || 0;
    purchasesPagination.hasNext = Boolean(pagination.hasNext);
  } catch (error) {
    console.error("Failed to load purchases", error);
    purchasesError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load purchases. Please try again.";
  } finally {
    purchasesLoading.value = false;
  }
};

const loadPlanUsage = async () => {
  if (!user.value?.id) return;
  planUsageLoading.value = true;
  planUsageError.value = "";
  try {
    const data = await fetchUserPlanUsage({ userId: user.value.id });
    planUsage.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to load plan usage", error);
    planUsageError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load plan usage. Please try again.";
  } finally {
    planUsageLoading.value = false;
  }
};

const loadWallet = async () => {
  if (!user.value?.id) return;
  walletLoading.value = true;
  walletError.value = "";
  try {
    wallet.value = await fetchUserWallet({ userId: user.value.id });
  } catch (error) {
    console.error("Failed to load wallet", error);
    walletError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load wallet. Please try again.";
  } finally {
    walletLoading.value = false;
  }
};

const openAddTransactionModal = () => {
  newTransaction.type = "credit";
  newTransaction.amount = "";
  newTransaction.description = "";
  addTransactionError.value = "";
  showAddTransactionModal.value = true;
};

const closeAddTransactionModal = () => {
  if (isAddingTransaction.value) return;
  showAddTransactionModal.value = false;
  addTransactionError.value = "";
};

const submitAddTransaction = async () => {
  if (!user.value?.id || isAddingTransaction.value) return;
  const amount = Number(newTransaction.amount);
  if (!newTransaction.amount || Number.isNaN(amount) || amount <= 0) {
    addTransactionError.value = "Enter a valid positive amount.";
    return;
  }
  if (!newTransaction.description.trim()) {
    addTransactionError.value = "Description is required.";
    return;
  }
  addTransactionError.value = "";
  isAddingTransaction.value = true;
  try {
    const updatedWallet = await addWalletTransaction(user.value.id, {
      amount: newTransaction.amount,
      type: newTransaction.type,
      description: newTransaction.description.trim(),
    });
    wallet.value = updatedWallet;
    showAddTransactionModal.value = false;
    statusMessage.value = "Wallet transaction added";
    statusType.value = "success";
  } catch (error) {
    console.error("Failed to add wallet transaction", error);
    addTransactionError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to add transaction. Please try again.";
  } finally {
    isAddingTransaction.value = false;
  }
};

const normalizeMedia = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const parseDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const labels = Array.isArray(raw.labels)
    ? raw.labels
      .map((label) => ({
        label: label?.label || "",
        score:
          label?.score != null && Number.isFinite(Number(label.score))
            ? Number(label.score)
            : null,
        value: label?.value,
      }))
      .filter((label) => label.label)
      .sort((a, b) => {
        if (a.label === "image_type") return -1;
        if (b.label === "image_type") return 1;
        if (a.label === "person_engagement") return -1;
        if (b.label === "person_engagement") return 1;
        if (a.label === "face_count") return -1;
        if (b.label === "face_count") return 1;
        return 0;
      })
    : [];
  const references = Array.isArray(raw.references)
    ? raw.references
      .map((reference) => ({
        type: reference?.type || "",
        id: reference?.id ? String(reference.id) : "",
      }))
      .filter((reference) => reference.type && reference.id)
    : [];
  return {
    path: raw.path ? String(raw.path) : "",
    userId: raw.userId ? String(raw.userId) : "",
    type: raw.type || "",
    labels,
    references,
    createdAt: parseDate(raw.createdAt),
    labelExtractedAt: parseDate(raw.labelExtractedAt),
    metadata:
      raw.metadata && typeof raw.metadata === "object" ? raw.metadata : null,
  };
};

const mediaReferenceLabel = (reference) => {
  if (!reference) return "";
  if (reference.type === "userAtom") return "Profile";
  if (reference.type === "message") return "Message";
  return reference.type;
};

const mediaReferenceUrl = (reference, ownerUserId) => {
  if (!reference) return null;
  if (reference.type === "userAtom") {
    return `/users/${reference.id}`;
  }
  if (reference.type === "message") {
    const ownerId = ownerUserId ? String(ownerUserId) : "";
    if (!ownerId) return null;
    const query = new URLSearchParams();
    query.append("participantIds[]", ownerId);
    query.append("messageId", reference.id);
    return `/messages?${query.toString()}`;
  }
  return null;
};

const loadMedia = async (page = 1) => {
  if (!user.value?.id) return;
  mediaLoading.value = true;
  mediaError.value = "";
  try {
    const payload = await fetchUserMedia({
      userId: user.value.id,
      page,
      pageSize: mediaPagination.pageSize,
    });
    const items = Array.isArray(payload?.items) ? payload.items : [];
    mediaItems.value = items.map((item) => normalizeMedia(item)).filter(Boolean);
    const pagination = payload?.pagination || {};
    mediaPagination.page = pagination.page || page;
    mediaPagination.pageSize = pagination.pageSize || mediaPagination.pageSize;
    mediaPagination.totalItems = pagination.totalItems || 0;
    mediaPagination.totalPages = pagination.totalPages || 0;
    mediaPagination.hasNext = Boolean(pagination.hasNext);
  } catch (error) {
    console.error("Failed to load media", error);
    mediaError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load media. Please try again.";
  } finally {
    mediaLoading.value = false;
  }
};

const parseSession = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw?.id ? String(raw.id) : "",
    deviceId: raw?.deviceId || "",
    platform: raw?.platform || "",
    appVersion: raw?.appVersion || "",
    language: raw?.language || raw?.appLanguage || "",
    timezone: raw?.timezone || "",
    loginCount: toInt(raw?.loginCount),
    fcmTokenErrorCount: toInt(raw?.fcmTokenErrorCount),
    lastLoginAt: parseDate(raw?.lastLoginAt),
    loggedOutAt: parseDate(raw?.loggedOutAt),
    createdAt: parseDate(raw?.createdAt),
    ips: stringList(raw?.ips),
    countries: stringList(raw?.countries),
    simCountries: stringList(raw?.simCountries),
    systemLocales: stringList(raw?.systemLocales),
  };
};

const loadSessions = async () => {
  if (!user.value?.id) return;
  sessionsLoading.value = true;
  sessionsError.value = "";
  try {
    const payload = await fetchUserSessions(user.value.id);
    const items = Array.isArray(payload?.items) ? payload.items : [];
    sessions.value = items.map((item) => parseSession(item)).filter(Boolean);
  } catch (error) {
    console.error("Failed to load sessions", error);
    sessionsError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load sessions. Please try again.";
  } finally {
    sessionsLoading.value = false;
  }
};

const handleDeleteMediaLabel = async (mediaPath, label, value) => {
  const valueDisplay = value != null ? `: ${value}` : "";
  if (!confirm(`Delete label "${label}${valueDisplay}" from this media?`)) return;
  try {
    await deleteMediaLabel(mediaPath, label, value);
    await loadMedia(mediaPagination.page);
  } catch (error) {
    alert(error?.payload?.message || error?.message || "Failed to delete label.");
  }
};

const refreshUserPurchases = async () => {
  if (!user.value?.id) return;
  purchasesLoading.value = true;
  purchasesError.value = "";
  try {
    await fetchRefreshUserPurchases(user.value.id);
    await loadPurchases(1);
    statusMessage.value = "User purchases refreshed.";
    statusType.value = "success";
  } catch (error) {
    console.error("Failed to refresh user purchases", error);
    purchasesError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to refresh user purchases. Please try again.";
  } finally {
    purchasesLoading.value = false;
  }
};

const toDatetimeLocalInput = (date) => {
  if (!date) return "";
  const pad = (n) => `${n}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const openEditPurchasePeriodModal = (purchase) => {
  if (!purchase?.id) return;
  editingPurchase.id = purchase.id;
  editingPurchase.fromDatetime = toDatetimeLocalInput(purchase.fromDatetime);
  editingPurchase.toDatetime = toDatetimeLocalInput(purchase.toDatetime);
  editPurchasePeriodError.value = "";
  showEditPurchasePeriodModal.value = true;
};

const setPurchasePeriodFieldToNow = (field) => {
  if (isSavingPurchasePeriod.value) return;
  editingPurchase[field] = toDatetimeLocalInput(new Date());
};

const closeEditPurchasePeriodModal = () => {
  if (isSavingPurchasePeriod.value) return;
  showEditPurchasePeriodModal.value = false;
  editPurchasePeriodError.value = "";
};

const submitEditPurchasePeriod = async () => {
  if (!user.value?.id || !editingPurchase.id || isSavingPurchasePeriod.value) {
    return;
  }
  const fromDate = editingPurchase.fromDatetime ? new Date(editingPurchase.fromDatetime) : null;
  const toDate = editingPurchase.toDatetime ? new Date(editingPurchase.toDatetime) : null;
  if (fromDate && toDate && fromDate >= toDate) {
    editPurchasePeriodError.value = "From date must be before to date.";
    return;
  }
  editPurchasePeriodError.value = "";
  isSavingPurchasePeriod.value = true;
  try {
    await updateUserPurchasePeriod(user.value.id, editingPurchase.id, {
      fromDatetime: fromDate ? fromDate.toISOString() : null,
      toDatetime: toDate ? toDate.toISOString() : null,
    });
    showEditPurchasePeriodModal.value = false;
    statusMessage.value = "Purchase period updated.";
    statusType.value = "success";
    await loadPurchases(purchasesPagination.page);
  } catch (error) {
    console.error("Failed to update purchase period", error);
    editPurchasePeriodError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to update purchase period. Please try again.";
  } finally {
    isSavingPurchasePeriod.value = false;
  }
};

const openEditUsageModal = (plan, feature) => {
  editingUsage.planId = plan.planId;
  editingUsage.planName = plan.planName || plan.planId;
  editingUsage.payableFeature = feature.payableFeature;
  editingUsage.usageCount = feature.usageCount ?? 0;
  editUsageError.value = "";
  showEditUsageModal.value = true;
};

const closeEditUsageModal = () => {
  if (isSavingUsage.value) return;
  showEditUsageModal.value = false;
  editUsageError.value = "";
};

const submitEditUsage = async () => {
  if (!user.value?.id || isSavingUsage.value) return;
  const usageCount = Number(editingUsage.usageCount);
  if (!Number.isFinite(usageCount) || usageCount < 0) {
    editUsageError.value = "Usage must be a non-negative number.";
    return;
  }
  editUsageError.value = "";
  isSavingUsage.value = true;
  try {
    await setUserPlanFeatureUsage(
      user.value.id,
      editingUsage.planId,
      editingUsage.payableFeature,
      usageCount
    );
    showEditUsageModal.value = false;
    statusMessage.value = "Feature usage updated.";
    statusType.value = "success";
    await loadPlanUsage();
  } catch (error) {
    console.error("Failed to update feature usage", error);
    editUsageError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to update feature usage. Please try again.";
  } finally {
    isSavingUsage.value = false;
  }
};

const TRANSFERABLE_PURCHASE_STATUSES = [
  "active",
  "subscription_trialing",
  "subscription_in_grace_period",
];

const isPurchaseTransferable = (purchase) => {
  if (!purchase) return false;
  if (!TRANSFERABLE_PURCHASE_STATUSES.includes(purchase.status)) return false;
  if (!purchase.toDatetime) return false;
  return purchase.toDatetime.getTime() > Date.now();
};

const openTransferPlanModal = (purchase) => {
  if (!purchase?.id) return;
  transferringPurchase.id = purchase.id;
  transferringPurchase.planId = purchase.planId;
  transferDestinationUserId.value = "";
  transferResolvedUser.value = null;
  transferLookupError.value = "";
  transferPlanError.value = "";
  showTransferPlanModal.value = true;
};

const closeTransferPlanModal = () => {
  if (isTransferringPlan.value) return;
  showTransferPlanModal.value = false;
};

watch(transferDestinationUserId, () => {
  transferResolvedUser.value = null;
});

const lookupTransferDestinationUser = async () => {
  const destinationUserId = transferDestinationUserId.value.trim();
  transferResolvedUser.value = null;
  transferLookupError.value = "";
  if (!destinationUserId) {
    transferLookupError.value = "Enter a user ID.";
    return;
  }
  if (destinationUserId === user.value?.id) {
    transferLookupError.value = "Cannot transfer a plan to the same user.";
    return;
  }
  isLookingUpTransferUser.value = true;
  try {
    const payload = await fetchUserById(destinationUserId);
    transferResolvedUser.value = {
      id: payload?.id ? String(payload.id) : "",
      name: payload?.name || "",
    };
  } catch (error) {
    transferLookupError.value =
      error?.payload?.message || error?.message || "User not found.";
  } finally {
    isLookingUpTransferUser.value = false;
  }
};

const submitTransferPlan = async () => {
  if (
    !user.value?.id ||
    !transferringPurchase.id ||
    !transferResolvedUser.value?.id ||
    isTransferringPlan.value
  ) {
    return;
  }
  transferPlanError.value = "";
  isTransferringPlan.value = true;
  try {
    await transferUserPlan(
      user.value.id,
      transferringPurchase.id,
      transferResolvedUser.value.id
    );
    showTransferPlanModal.value = false;
    statusMessage.value = "Plan transferred successfully.";
    statusType.value = "success";
    await loadPurchases(purchasesPagination.page);
  } catch (error) {
    console.error("Failed to transfer plan", error);
    transferPlanError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to transfer plan. Please try again.";
  } finally {
    isTransferringPlan.value = false;
  }
};

const getSessionJwt = async (session) => {
  if (!user.value?.id || !session?.id || sessionJwtLoading[session.id]) return;
  if (session.loggedOutAt) {
    statusMessage.value = "Cannot get JWT for a logged out session.";
    statusType.value = "danger";
    return;
  }
  const sessionId = session.id;
  sessionJwtLoading[sessionId] = true;
  statusMessage.value = "";
  try {
    const payload = await fetchUserSessionToken(user.value.id, sessionId);
    const jwtToken =
      payload?.jwtToken ||
      payload?.jwt_token ||
      payload?.token ||
      (typeof payload === "string" ? payload : "");
    if (!jwtToken) {
      throw new Error("JWT token not found in response.");
    }

    const label = session.deviceId || session.platform || sessionId;
    let copied = false;
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(jwtToken);
        copied = true;
      } catch (copyError) {
        console.warn("Failed to copy JWT to clipboard", copyError);
      }
    }

    if (copied) {
      statusMessage.value = `JWT copied to clipboard for session ${label}.`;
    } else {
      if (typeof window !== "undefined" && window.prompt) {
        window.prompt("Session JWT", jwtToken);
      }
      statusMessage.value =
        `JWT fetched for session ${label}. Copy it from the prompt.`;
    }
    statusType.value = "success";
  } catch (error) {
    console.error("Failed to get session JWT", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to get session JWT. Please try again.";
    statusType.value = "danger";
  } finally {
    sessionJwtLoading[sessionId] = false;
  }
};

const resetMatchQueryState = () => {
  matchQuery.value = "";
  matchQueryError.value = "";
  matchQueryStatus.value = "";
  matchQueryFetched.value = false;
  isMatchQueryLoading.value = false;
  if (matchQueryStatusTimeout) {
    clearTimeout(matchQueryStatusTimeout);
    matchQueryStatusTimeout = null;
  }
};

const setMatchQueryStatus = (message) => {
  matchQueryStatus.value = message;
  if (matchQueryStatusTimeout) {
    clearTimeout(matchQueryStatusTimeout);
  }
  matchQueryStatusTimeout = window.setTimeout(() => {
    matchQueryStatus.value = "";
  }, 2000);
};

const loadMatchQuery = async () => {
  if (!user.value?.id || isMatchQueryLoading.value) return;
  matchQueryError.value = "";
  matchQueryStatus.value = "";
  if (matchQueryStatusTimeout) {
    clearTimeout(matchQueryStatusTimeout);
    matchQueryStatusTimeout = null;
  }
  isMatchQueryLoading.value = true;
  try {
    const queryText = await fetchUserMatchQuery(user.value.id);
    if (typeof queryText === "string") {
      matchQuery.value = queryText;
    } else if (queryText?.query) {
      matchQuery.value = String(queryText.query);
    } else {
      matchQuery.value =
        queryText != null ? JSON.stringify(queryText, null, 2) : "";
    }
    matchQueryFetched.value = true;
  } catch (error) {
    console.error("Failed to load match query", error);
    matchQueryError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load match query. Please try again.";
  } finally {
    isMatchQueryLoading.value = false;
  }
};

const copyMatchQuery = async () => {
  if (!matchQuery.value) return;
  if (!navigator?.clipboard?.writeText) {
    matchQueryError.value =
      "Clipboard access is not available. Please copy manually.";
    return;
  }
  try {
    await navigator.clipboard.writeText(matchQuery.value);
    matchQueryError.value = "";
    setMatchQueryStatus("Copied to clipboard");
  } catch (error) {
    console.error("Failed to copy match query", error);
    matchQueryError.value =
      "Failed to copy match query. Please copy it manually.";
  }
};

const fetchUser = async () => {
  isLoading.value = true;
  errorMessage.value = "";
  statusMessage.value = "";
  Object.keys(sessionJwtLoading).forEach((key) => {
    delete sessionJwtLoading[key];
  });
  const id = route.params.id;
  if (!id) {
    errorMessage.value = "User id is missing from the route.";
    isLoading.value = false;
    return;
  }
  try {
    const payload = await fetchUserById(id);
    user.value = normalizeUser(payload);
    hydrateDrafts();
    await loadSessions();
  } catch (error) {
    console.error("Failed to load user", error);
    errorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load user. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const loadVerificationAttempts = async () => {
  if (!user.value?.id) return;
  isVerificationLoading.value = true;
  verificationErrorMessage.value = "";
  try {
    const payload = await fetchVerificationAttempts(user.value.id);
    const items = Array.isArray(payload) ? payload : [];
    const parsed = items
      .map((item) => parseVerificationAttempt(item))
      .filter(Boolean);
    verificationAttempts.value = parsed;
    parsed.forEach((attempt) => {
      const defaultNext = attempt.adminNextOptions?.[0] || "";
      if (attempt.id) {
        attemptNextStatus[attempt.id] = defaultNext;
      }
    });
  } catch (error) {
    console.error("Failed to load verification attempts", error);
    verificationErrorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load verification attempts. Please try again.";
  } finally {
    isVerificationLoading.value = false;
  }
};

const normalizeUser = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const parseAtoms = (atomsList) =>
    Array.isArray(atomsList)
      ? atomsList.map((atom) => ({
        id: atom?.id ? String(atom.id) : "",
        attribute: atom?.attribute || "",
        order: toInt(atom?.order),
        textValue: atom?.textValue,
        intValue: toInt(atom?.intValue),
        mediaPath: atom?.mediaPath || atom?.media_path || "",
        metadata: atom?.metadata && typeof atom.metadata === "object"
          ? atom.metadata
          : null,
      }))
      : [];

  const parseMatchFilter = (filter) => {
    if (!filter || typeof filter !== "object") return null;
    const toNullableBool = (value) =>
      value === null || value === undefined ? null : toBool(value);
    return {
      minAge: toInt(filter.minAge),
      maxAge: toInt(filter.maxAge),
      maxDistance: toInt(filter.maxDistance),
      alsoShowFurtherAwayDistance: toInt(filter.alsoShowFurtherAwayDistance),
      ageFilterDealBreaker: toNullableBool(filter.ageFilterDealBreaker),
      genders: stringList(filter.genders),
      onlyActiveUsers: toNullableBool(filter.onlyActiveUsers),
      onlyVerifiedUsers: toNullableBool(filter.onlyVerifiedUsers),
      countries: stringList(filter.countries),
    };
  };

  const birthdate =
    parseDate(raw.birthdate) || parseDate(raw.birthday) || null;

  return {
    id: raw.id ? String(raw.id) : "",
    name:
      raw.name ||
      raw.fullName ||
      raw.displayName ||
      [raw.firstName, raw.lastName]
        .filter(Boolean)
        .map((v) => String(v))
        .join(" ")
        .trim() ||
      "Unknown",
    gender: raw.gender || "--",
    status: raw.status || raw.state || "--",
    verificationStatus: raw.verificationStatus || raw.verifyStatus || "",
    email: raw.email || "",
    isEmailVerified: toBool(raw.isEmailVerified),
    phone: raw.phone || "",
    isPhoneVerified: toBool(raw.isPhoneVerified),
    lastLoginDate:
      parseDate(raw.lastLoginDatetime) ||
      parseDate(raw.lastLoginAt) ||
      parseDate(raw.lastLoginDate),
    createdAt: parseDate(raw.createdAt),
    updatedAt: parseDate(raw.updatedAt),
    deletedAt: parseDate(raw.deletedAt),
    birthday: birthdate,
    age: toInt(raw.age) ?? calculateAge(birthdate),
    city: raw.city,
    country: raw.country,
    address: raw.address || {},
    isPaused: toBool(raw.isPaused),
    pausedAt: parseDate(raw.pausedAt),
    matchScoreMultiplier: toInt(raw.matchScoreMultiplier),
    profileQualityScore: toInt(raw.profileQualityScore),
    invalidityReasons: raw.invalidityReasons || [],
    atoms: parseAtoms(raw.atoms),
    registrationMetadata:
      raw.registrationMetadata && typeof raw.registrationMetadata === "object"
        ? raw.registrationMetadata
        : null,
    locationLabel: raw.locationLabel,
    matchFilter: parseMatchFilter(raw.matchFilter),
  };
};

const hydrateDrafts = () => {
  if (!user.value) return;
  draft.name = user.value.name || "";
  draft.status = user.value.status || "";
  draft.gender = user.value.gender || "";
  draft.verificationStatus = user.value.verificationStatus || "";
  draft.matchScoreMultiplier =
    user.value.matchScoreMultiplier != null
      ? String(user.value.matchScoreMultiplier)
      : "";
  draft.birthday = user.value.birthday
    ? formatDateOnly(user.value.birthday)
    : "";
};

const startEdit = (key) => {
  editing[key] = true;
  statusMessage.value = "";
};

const cancelEdit = (key) => {
  hydrateDrafts();
  editing[key] = false;
  statusMessage.value = "";
  if (key === "status") statusReason.value = "";
};

const saveStatus = async () => {
  const value = draft.status;
  if (!value || value === user.value?.status) {
    editing.status = false;
    statusReason.value = "";
    return;
  }
  if (!statusReason.value.trim()) {
    statusMessage.value = "A reason is required to change a user status.";
    statusType.value = "danger";
    return;
  }
  saving.status = true;
  statusMessage.value = "";
  try {
    const updated = await updateUserStatus(
      user.value.id,
      value,
      statusReason.value
    );
    user.value = normalizeUser(updated);
    hydrateDrafts();
    editing.status = false;
    statusReason.value = "";
    statusMessage.value = "Status updated";
    statusType.value = "success";
    activityLogRef.value?.reload();
  } catch (error) {
    console.error("Failed to update status", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to update status. Please try again.";
    statusType.value = "danger";
  } finally {
    saving.status = false;
  }
};

const saveField = async (key) => {
  if (!user.value?.id || saving[key]) return;
  if (key === "status") return saveStatus();
  const payload = buildPayload(key);
  if (!payload) return;
  saving[key] = true;
  statusMessage.value = "";
  try {
    const updated = await updateUser(user.value.id, payload);
    user.value = normalizeUser(updated);
    hydrateDrafts();
    editing[key] = false;
    statusMessage.value = "Saved successfully";
    statusType.value = "success";
  } catch (error) {
    console.error(`Failed to save ${key}`, error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      `Failed to save ${key}. Please try again.`;
    statusType.value = "danger";
  } finally {
    saving[key] = false;
  }
};

const buildPayload = (key) => {
  const current = user.value || {};
  switch (key) {
    case "name": {
      const value = (draft.name || "").trim();
      if (!value) return null;
      if (value === current.name) return null;
      return { name: value };
    }
    case "gender": {
      const value = draft.gender;
      if (!value || value === current.gender) return null;
      return { gender: value };
    }
    case "verificationStatus": {
      const value = draft.verificationStatus;
      if (!value || value === current.verificationStatus) return null;
      return { verificationStatus: value };
    }
    case "matchScore": {
      const value = (draft.matchScoreMultiplier || "").trim();
      if (!value) return null;
      const num = Number(value);
      if (!Number.isFinite(num) || num < 0) return null;
      if (num === current.matchScoreMultiplier) return null;
      return { matchScoreMultiplier: num };
    }
    case "birthday": {
      const value = draft.birthday;
      const date = value ? parseDate(value) : null;
      const currentDate = current.birthday ? parseDate(current.birthday) : null;
      const changed =
        (!date && currentDate) ||
        (date && !currentDate) ||
        (date &&
          currentDate &&
          date.toISOString().slice(0, 10) !==
          currentDate.toISOString().slice(0, 10));
      if (!changed) return null;
      return { birthdate: date ? date.toISOString() : null };
    }
  }
  return null;
};

const deriveDistinct = (items, key) => {
  const set = new Set();
  items.forEach((item) => {
    if (item?.[key]) {
      set.add(String(item[key]));
    }
  });
  return Array.from(set);
};

const stringList = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (item == null ? "" : String(item)))
    .filter((item) => item.trim());
};

const toInt = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
};

const calculateAge = (birthday) => {
  if (!birthday) return null;
  const now = new Date();
  let age = now.getFullYear() - birthday.getFullYear();
  const hasHadBirthday =
    now.getMonth() > birthday.getMonth() ||
    (now.getMonth() === birthday.getMonth() &&
      now.getDate() >= birthday.getDate());
  if (!hasHadBirthday) age -= 1;
  return age >= 0 ? age : null;
};

const goBack = () => router.push({ name: "Users" });

const relationsHref = computed(() => {
  if (!user.value?.id) return null;
  return router.resolve({
    name: "UserRelations",
    query: { userIds: user.value.id },
  }).href;
});

const messagesHref = computed(() => {
  if (!user.value?.id) return null;
  return router.resolve({
    name: "Messages",
    query: { "participantIds[]": user.value.id },
  }).href;
});

const complaintsHref = computed(() => {
  if (!user.value?.id) return null;
  return router.resolve({
    name: "ComplaintDetail",
    query: {},
  }).href;
});

const supportMessagesHref = computed(() => {
  if (!user.value?.id) return null;
  return router.resolve({
    name: "Support",
    query: { userId: user.value.id },
  }).href;
});

const referralHref = computed(() => {
  if (!user.value?.id) return null;
  return router.resolve({
    name: "Referral",
    query: { search: user.value.id },
  }).href;
});

const pushClientEvents = async () => {
  if (!user.value?.id || isPushingClientEvents.value) return;
  isPushingClientEvents.value = true;
  statusMessage.value = "";
  try {
    await pushUserClientEvents(user.value.id);
    statusMessage.value = "Client events pushed.";
    statusType.value = "success";
  } catch (error) {
    console.error("Failed to push client events", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to push client events. Please try again.";
    statusType.value = "danger";
  } finally {
    isPushingClientEvents.value = false;
  }
};

const openDeleteModal = () => {
  deleteConfirmation.value = "";
  deleteReason.value = "";
  deleteError.value = "";
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  if (isDeleting.value) return;
  showDeleteModal.value = false;
  deleteConfirmation.value = "";
  deleteReason.value = "";
  deleteError.value = "";
};

const confirmDelete = async () => {
  if (!user.value?.id || isDeleting.value) return;
  if (deleteConfirmation.value.trim().toUpperCase() !== "DELETE") {
    deleteError.value = 'Type "DELETE" to confirm.';
    return;
  }
  if (!deleteReason.value.trim()) {
    deleteError.value = "A reason is required to delete a user.";
    return;
  }
  deleteError.value = "";
  isDeleting.value = true;
  try {
    await deleteUser(user.value.id, deleteReason.value);
    statusMessage.value = "User deleted";
    statusType.value = "success";
    showDeleteModal.value = false;
    await router.push({ name: "Users" });
  } catch (error) {
    console.error("Failed to delete user", error);
    deleteError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to delete user. Please try again.";
  } finally {
    isDeleting.value = false;
  }
};

const openVerificationPhoto = async (attempt, index) => {
  if (!attempt?.id || typeof index !== "number") return;
  try {
    const payload = await fetchVerificationMedia(attempt.id, index);
    if (!(payload?.blob instanceof Blob)) {
      statusMessage.value = "Unable to load verification photo. Please try again.";
      statusType.value = "danger";
      return;
    }
    if (currentMediaObjectUrl.value) {
      URL.revokeObjectURL(currentMediaObjectUrl.value);
      currentMediaObjectUrl.value = "";
    }
    const objectUrl = URL.createObjectURL(payload.blob);
    currentMediaObjectUrl.value = objectUrl;
    statusMessage.value = "";
    openMedia(objectUrl);
  } catch (error) {
    console.error("Failed to load verification photo", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load verification photo. Please try again.";
    statusType.value = "danger";
  }
};

const updateAttemptStatus = async (attempt, overrideStatus) => {
  if (!attempt?.id) return;
  const nextStatus = overrideStatus || attemptNextStatus[attempt.id];
  if (!nextStatus) return;
  updatingAttempt[attempt.id] = true;
  statusMessage.value = "";
  try {
    await updateVerificationAttemptStatus(attempt.id, nextStatus);
    await loadVerificationAttempts();
    statusMessage.value = "Verification attempt status updated.";
    statusType.value = "success";
  } catch (error) {
    console.error("Failed to update verification attempt", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to update verification attempt. Please try again.";
    statusType.value = "danger";
  } finally {
    updatingAttempt[attempt.id] = false;
  }
};

const openMedia = (url) => {
  if (!url) return;
  currentMediaUrl.value = url;
  showMediaModal.value = true;
};

const closeMedia = () => {
  showMediaModal.value = false;
  currentMediaUrl.value = "";
  if (currentMediaObjectUrl.value) {
    URL.revokeObjectURL(currentMediaObjectUrl.value);
    currentMediaObjectUrl.value = "";
  }
};

const applyTabFromRoute = () => {
  const desiredTab = normalizeTabKey(route.query?.tab);
  activeTab.value = desiredTab;
};

const selectTab = (tabKey) => {
  const normalized = normalizeTabKey(tabKey);
  activeTab.value = normalized;
  const nextQuery = { ...route.query, tab: normalized };
  router.replace({
    name: "UserDetail",
    params: { id: route.params.id },
    query: nextQuery,
  });
  fetchUser();
  if (normalized === "verification") {
    loadVerificationAttempts();
  }
  if (normalized === "purchases") {
    loadPurchases(1);
    loadWallet();
  }
  if (normalized === "media") {
    loadMedia(1);
  }
};

applyTabFromRoute();
onMounted(async () => {
  await fetchUser();
  const desiredTab = normalizeTabKey(route.query?.tab);
  console.log("Desired tab on mount:", desiredTab);
  if (desiredTab === "verification") {
    loadVerificationAttempts();
  }
  if (desiredTab === "purchases") {
    loadPurchases(1);
    loadWallet();
  }
  if (desiredTab === "media") {
    loadMedia(1);
  }
});
watch(
  () => route.params.id,
  () => {
    applyTabFromRoute();
    verificationAttempts.value = [];
    mediaItems.value = [];
    mediaError.value = "";
    mediaPagination.page = 1;
    mediaPagination.totalItems = 0;
    mediaPagination.totalPages = 0;
    sessions.value = [];
    sessionsError.value = "";
    resetMatchQueryState();
  }
);
watch(
  () => route.query.tab,
  () => {
    applyTabFromRoute();
  }
);

watch(
  () => activeTab.value,
  (value) => {
    if (value === "verification" && !isVerificationLoading.value) {
      loadVerificationAttempts();
    }
    if (value === "purchases" && !purchasesLoading.value) {
      loadPurchases(purchasesPagination.page);
      loadPlanUsage();
      loadWallet();
    }
    if (value === "media" && !mediaLoading.value) {
      loadMedia(mediaPagination.page);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="py-4 container-fluid">
    <div v-if="isLoading" class="card">
      <div class="card-body text-center py-5">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 mb-0 text-sm text-secondary">Loading user profile...</p>
      </div>
    </div>

    <div v-else-if="errorMessage" class="card">
      <div class="card-body text-center py-5">
        <div class="icon icon-shape bg-gradient-danger text-white shadow text-center border-radius-md mx-auto">
          <i class="ni ni-fat-remove"></i>
        </div>
        <p class="mt-3 text-sm">{{ errorMessage }}</p>
        <button class="btn btn-sm btn-success" type="button" @click="fetchUser">
          Try again
        </button>
      </div>
    </div>

    <div v-else-if="!user" class="card">
      <div class="card-body text-center py-5">
        <div class="icon icon-shape bg-gradient-secondary text-white shadow text-center border-radius-md mx-auto">
          <i class="ni ni-single-02"></i>
        </div>
        <p class="mt-3 text-sm">User not found.</p>
        <button class="btn btn-sm btn-outline-secondary" type="button" @click="goBack">
          Back to users
        </button>
      </div>
    </div>

    <div v-else>
      <div class="mb-4">
        <ul class="nav nav-pills nav-fill bg-gray-100 p-1 rounded-pill">
          <li v-for="tab in tabs" :key="tab.key" class="nav-item">
            <button type="button" class="nav-link mb-0 rounded-pill" :class="{ active: activeTab === tab.key }"
              @click="selectTab(tab.key)">
              {{ tab.label }}
            </button>
          </li>
        </ul>
        <argon-alert v-if="statusMessage && !isLoading" class="mt-3" :color="statusType">
          {{ statusMessage }}
        </argon-alert>
      </div>
      <div class="">
        <!-- Profile tab -->
        <div v-if="activeTab === 'profile'">
          <div class="row g-3">
            <div class="col-12 col-lg-5">
              <div class="card border shadow-sm h-100">
                <div class="card-body">
                  <div
                    class="avatar avatar-xl rounded-circle bg-light border d-flex align-items-center justify-content-center overflow-hidden">
                    <img v-if="firstPhotoUrl" :src="firstPhotoUrl" alt="avatar" class="w-100 h-100 object-fit-cover" />
                    <i v-else class="ni ni-circle-08 text-success"></i>
                  </div>
                  <div class="my-2">
                    <div v-if="editing.name" class="d-flex flex-column gap-2">
                      <input type="text" class="form-control form-control-sm" v-model="draft.name"
                        placeholder="Full name" />
                      <div class="d-flex gap-2">
                        <button class="btn btn-sm btn-success" :disabled="saving.name" @click="saveField('name')">
                          Save
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" :disabled="saving.name"
                          @click="cancelEdit('name')">
                          Cancel
                        </button>
                      </div>
                    </div>
                    <div v-else class="d-flex align-items-center gap-2 flex-wrap">
                      <h6 class="mb-0">{{ user.name }}</h6>
                      <button v-if="canEditUser" class="btn btn-link text-secondary p-0 text-xs mb-0" type="button"
                        @click="startEdit('name')">
                        Edit
                      </button>
                    </div>
                  </div>
                  <div class="my-2">
                    <p class="text-xs text-secondary mb-1">ID: {{ user.id }}</p>
                  </div>
                  <div class="my-2">
                    <div class="d-flex align-items-center gap-2">
                      <span class="badge bg-light text-dark border text-xxs">
                        {{ user.status || "--" }}
                      </span>
                      <div v-if="editing.status" class="d-flex align-items-center gap-2 flex-wrap">
                        <select class="form-select form-select-sm" v-model="draft.status">
                          <option v-for="status in STATUS_OPTIONS" :key="status" :value="status">
                            {{ status.charAt(0).toUpperCase() + status.slice(1) }}
                          </option>
                        </select>
                        <input v-model="statusReason" type="text" class="form-control form-control-sm"
                          style="min-width: 220px" placeholder="Reason (required)"
                          :disabled="saving.status" @keyup.enter="saveField('status')" />
                        <button class="btn btn-xs btn-success mb-0"
                          :disabled="saving.status || !statusReason.trim()" @click="saveField('status')">
                          Save
                        </button>
                        <button class="btn btn-xs btn-outline-secondary mb-0" :disabled="saving.status"
                          @click="cancelEdit('status')">
                          Cancel
                        </button>
                      </div>
                      <button v-else-if="user.status !== 'deleted' && canEditUser"
                        class="btn btn-link text-secondary p-0 text-xs mb-0" type="button" @click="startEdit('status')">
                        Edit
                      </button>
                      <span v-else-if="user.status === 'deleted'" class="text-xs text-secondary">
                        Status locked (deleted)
                      </span>
                    </div>
                  </div>

                  <div class="row g-2">
                    <div class="col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Gender</h6>
                      <div v-if="editing.gender" class="d-flex align-items-center gap-2">
                        <select class="form-select form-select-sm" v-model="draft.gender">
                          <option value="">Not specified</option>
                          <option v-for="gender in GENDER_OPTIONS" :key="gender" :value="gender">
                            {{ gender.charAt(0).toUpperCase() + gender.slice(1) }}
                          </option>
                        </select>
                        <button class="btn btn-xs btn-success mb-0" :disabled="saving.gender"
                          @click="saveField('gender')">
                          Save
                        </button>
                        <button class="btn btn-xs btn-outline-secondary mb-0" :disabled="saving.gender"
                          @click="cancelEdit('gender')">
                          Cancel
                        </button>
                      </div>
                      <div v-else class="d-flex align-items-center gap-2">
                        <p class="mb-0 text-sm">{{ user.gender || "--" }}</p>
                        <button v-if="canEditUser" class="btn btn-link text-secondary p-0 text-xs mb-0" type="button"
                          @click="startEdit('gender')">
                          Edit
                        </button>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Verification</h6>
                      <div v-if="editing.verificationStatus" class="d-flex align-items-center gap-2">
                        <select class="form-select form-select-sm" v-model="draft.verificationStatus">
                          <option value="">Not specified</option>
                          <option v-for="status in VERIFICATION_STATUS_OPTIONS" :key="status" :value="status">
                            {{ status }}
                          </option>
                        </select>
                        <button class="btn btn-xs btn-success mb-0" :disabled="saving.verificationStatus"
                          @click="saveField('verificationStatus')">
                          Save
                        </button>
                        <button class="btn btn-xs btn-outline-secondary mb-0" :disabled="saving.verificationStatus"
                          @click="cancelEdit('verificationStatus')">
                          Cancel
                        </button>
                      </div>
                      <div v-else class="d-flex align-items-center gap-2">
                        <p class="mb-0 text-sm">{{ user.verificationStatus || "--" }}</p>
                        <button v-if="canEditUser" class="btn btn-link text-secondary p-0 text-xs mb-0" type="button"
                          @click="startEdit('verificationStatus')">
                          Edit
                        </button>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Location</h6>
                      <p class="mb-0 text-sm">{{ locationLabel }}</p>
                    </div>
                    <div class="col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Paused</h6>
                      <p class="mb-0 text-sm">{{ user.isPaused ? "Yes" : "No" }}</p>
                    </div>
                    <div class="col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Match score</h6>
                      <div v-if="editing.matchScore" class="d-flex align-items-center gap-2">
                        <input type="number" class="form-control form-control-sm" v-model="draft.matchScoreMultiplier"
                          min="0" />
                        <button class="btn btn-xs btn-success mb-0" :disabled="saving.matchScore"
                          @click="saveField('matchScore')">
                          Save
                        </button>
                        <button class="btn btn-xs btn-outline-secondary mb-0" :disabled="saving.matchScore"
                          @click="cancelEdit('matchScore')">
                          Cancel
                        </button>
                      </div>
                      <div v-else class="d-flex align-items-center gap-2">
                        <p class="mb-0 text-sm">
                          {{ user.matchScoreMultiplier ?? "--" }}
                        </p>
                        <button v-if="canEditUser" class="btn btn-link text-secondary p-0 text-xs mb-0" type="button"
                          @click="startEdit('matchScore')">
                          Edit
                        </button>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Profile quality</h6>
                      <p class="mb-0 text-sm">
                        {{ user.profileQualityScore ?? "--" }}
                      </p>
                    </div>
                    <div class="col-md-12">
                      <h6 class="text-xs text-secondary mb-1">Invalidity reasons</h6>
                      <div v-if="user.invalidityReasons && user.invalidityReasons.length">
                        <span v-for="(reason, idx) in user.invalidityReasons" :key="idx"
                          class="badge badge-sm bg-light text-dark border text-xs fw-light me-1 mb-1">
                          {{ reason.type }}
                          <span v-if="reason.atomReference" class="text-secondary">
                            · {{ reason.atomReference.attribute }}<span v-if="reason.atomReference.id"> ({{
                              reason.atomReference.id }})</span>
                          </span>
                        </span>
                      </div>
                      <p v-else class="mb-0 text-sm">--</p>
                    </div>
                    <div class="col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Age / Birthday</h6>
                      <div v-if="editing.birthday" class="d-flex align-items-center gap-2 flex-wrap">
                        <input type="date" class="form-control form-control-sm" v-model="draft.birthday" />
                        <button class="btn btn-xs btn-success mb-0" :disabled="saving.birthday"
                          @click="saveField('birthday')">
                          Save
                        </button>
                        <button class="btn btn-xs btn-outline-secondary mb-0" :disabled="saving.birthday"
                          @click="cancelEdit('birthday')">
                          Cancel
                        </button>
                      </div>
                      <div v-else class="d-flex align-items-center gap-2">
                        <p class="mb-0 text-sm">{{ ageLabel }}</p>
                        <button v-if="canEditUser" class="btn btn-link text-secondary p-0 text-xs mb-0" type="button"
                          @click="startEdit('birthday')">
                          Edit
                        </button>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Last login</h6>
                      <p class="mb-0 text-sm">{{ formatDateTime(user.lastLoginDate) }}</p>
                    </div>
                    <div class="col-auto">
                      <p class="mb-0 mt-3 text-sm">
                        <a v-if="relationsHref" :href="relationsHref" target="_blank" rel="noopener"
                          class="btn btn-sm btn-outline-info mb-1 me-2">
                          Relations
                        </a>
                        <a v-if="messagesHref" :href="messagesHref" target="_blank" rel="noopener"
                          class="btn btn-sm btn-outline-info mb-1 me-2">
                          Messages
                        </a>
                        <a v-if="complaintsHref" :href="complaintsHref" target="_blank" rel="noopener"
                          class="btn btn-sm btn-outline-info mb-1 me-2">
                          Complaints
                        </a>
                        <a v-if="referralHref" :href="referralHref" target="_blank" rel="noopener"
                          class="btn btn-sm btn-outline-info mb-1 me-2">
                          Referral
                        </a>
                        <a v-if="supportMessagesHref" :href="supportMessagesHref" target="_blank" rel="noopener"
                          class="btn btn-sm btn-outline-info mb-1 me-2">
                          Support messages
                        </a>
                      </p>
                    </div>
                    <div class="col-12">
                      <p class="mb-0 mt-3 text-sm">
                        <button v-if="canEditUser" type="button" class="btn btn-sm btn-outline-secondary mb-0 me-2"
                          @click="pushClientEvents" :disabled="isPushingClientEvents">
                          {{ isPushingClientEvents ? "Pushing..." : "Push client events" }}
                        </button>
                        <button v-if="canEditUser" type="button" class="btn btn-sm btn-outline-danger mb-0 me-2"
                          @click="openDeleteModal" :disabled="user.status === 'deleted' || isDeleting">
                          Delete user
                        </button>
                      </p>
                    </div>
                    <div class="col-12 border-top pt-3 mt-2">
                      <UserActivityLog ref="activityLogRef" :user-id="user.id" compact
                        title="Moderation history" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-7">
              <div class="card border shadow-sm h-100">
                <div class="card-body">
                  <h6 class="mb-3">Contact</h6>
                  <div class="row g-3 mb-3">
                    <div class="col-12 col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Email</h6>
                      <p class="mb-0 text-sm">{{ user.email || "--" }}</p>
                      <span class="badge bg-light text-dark border text-xxs">
                        {{ user.isEmailVerified ? "Verified" : "Unverified" }}
                      </span>
                    </div>
                    <div class="col-12 col-md-6">
                      <h6 class="text-xs text-secondary mb-0">Phone</h6>
                      <p class="mb-0 text-sm">{{ user.phone || "--" }}</p>
                      <span class="badge bg-light text-dark border text-xxs">
                        {{ user.isPhoneVerified ? "Verified" : "Unverified" }}
                      </span>
                    </div>
                  </div>

                  <h6 class="text-xs text-secondary">Platforms</h6>
                  <div class="d-flex flex-wrap gap-2 mb-3">
                    <span v-for="platform in platforms" :key="platform" class="badge bg-success text-white">
                      {{ platform }}
                    </span>
                    <span v-if="platforms.length === 0" class="text-xs text-secondary">
                      Not available
                    </span>
                  </div>

                  <h6 class="text-xs text-secondary">App versions</h6>
                  <div class="d-flex flex-wrap gap-2 mb-3">
                    <span v-for="version in appVersions" :key="version" class="badge bg-light text-dark border">
                      {{ version }}
                    </span>
                    <span v-if="appVersions.length === 0" class="text-xs text-secondary">
                      Not available
                    </span>
                  </div>

                  <h6 class="text-xs text-secondary">Languages</h6>
                  <div class="d-flex flex-wrap gap-2 mb-3">
                    <span v-for="lang in languages" :key="lang" class="badge bg-light text-dark border">
                      {{ lang }}
                    </span>
                    <span v-if="languages.length === 0" class="text-xs text-secondary">
                      Not available
                    </span>
                  </div>

                  <div class="row g-2">
                    <div class="col-12 col-md-6">
                      <p class="text-xs text-secondary mb-0">Created</p>
                      <p class="mb-0 text-sm">{{ formatDateTime(user.createdAt) }}</p>
                    </div>
                    <div class="col-12 col-md-6">
                      <p class="text-xs text-secondary mb-0">Updated</p>
                      <p class="mb-0 text-sm">{{ formatDateTime(user.updatedAt) }}</p>
                    </div>
                    <div class="col-12 col-md-6">
                      <p class="text-xs text-secondary mb-0">Deleted</p>
                      <p class="mb-0 text-sm">{{ formatDateTime(user.deletedAt) }}</p>
                    </div>
                    <div class="col-12 col-md-6">
                      <p class="text-xs text-secondary mb-0">Paused at</p>
                      <p class="mb-0 text-sm">{{ formatDateTime(user.pausedAt) }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card border shadow-sm mt-3">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div>
                  <h6 class="mb-1">Match query</h6>
                  <p class="text-xs text-secondary mb-0">
                    Mongo aggregation used when matching this user.
                  </p>
                </div>
                <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="isMatchQueryLoading"
                  @click="loadMatchQuery">
                  <span v-if="isMatchQueryLoading" class="spinner-border spinner-border-sm me-1"></span>
                  Show match query
                </button>
              </div>

              <div v-if="user.matchFilter" class="row g-3 mb-3">
                <div class="col-6 col-md-3">
                  <p class="text-xxs text-secondary text-uppercase mb-1">Age range</p>
                  <p class="text-sm mb-0">
                    {{ formatAgeRange(user.matchFilter.minAge, user.matchFilter.maxAge) }}
                    <span v-if="user.matchFilter.ageFilterDealBreaker" class="badge bg-danger text-white text-xxs ms-1">
                      Deal breaker
                    </span>
                  </p>
                </div>
                <div class="col-6 col-md-3">
                  <p class="text-xxs text-secondary text-uppercase mb-1">Max distance</p>
                  <p class="text-sm mb-0">
                    {{ formatDistanceKm(user.matchFilter.maxDistance) }}
                    <span v-if="Number.isFinite(user.matchFilter.alsoShowFurtherAwayDistance)"
                      class="text-xxs text-secondary ms-1">
                      (+{{ user.matchFilter.alsoShowFurtherAwayDistance }} km)
                    </span>
                  </p>
                </div>
                <div class="col-6 col-md-3">
                  <p class="text-xxs text-secondary text-uppercase mb-1">Genders</p>
                  <p class="text-sm mb-0">{{ formatList(user.matchFilter.genders) }}</p>
                </div>
                <div class="col-6 col-md-3">
                  <p class="text-xxs text-secondary text-uppercase mb-1">Countries</p>
                  <p class="text-sm mb-0">{{ formatList(user.matchFilter.countries) }}</p>
                </div>
                <div class="col-6 col-md-3">
                  <p class="text-xxs text-secondary text-uppercase mb-1">Only active users</p>
                  <p class="text-sm mb-0">{{ formatBoolean(user.matchFilter.onlyActiveUsers) }}</p>
                </div>
                <div class="col-6 col-md-3">
                  <p class="text-xxs text-secondary text-uppercase mb-1">Only verified users</p>
                  <p class="text-sm mb-0">{{ formatBoolean(user.matchFilter.onlyVerifiedUsers) }}</p>
                </div>
              </div>

              <argon-alert v-if="matchQueryError" color="danger" class="mb-3">
                {{ matchQueryError }}
              </argon-alert>

              <div v-if="isMatchQueryLoading && !matchQueryFetched" class="text-sm text-secondary">
                Loading match query...
              </div>
              <div v-else-if="matchQueryFetched">
                <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <span class="text-xs text-secondary">Query preview</span>
                  <div class="d-flex align-items-center gap-2">
                    <button class="btn btn-xs btn-outline-secondary mb-0" type="button" :disabled="!matchQuery"
                      @click="copyMatchQuery">
                      Copy
                    </button>
                    <span v-if="matchQueryStatus" class="text-success text-xxs">
                      {{ matchQueryStatus }}
                    </span>
                  </div>
                </div>
                <textarea class="form-control font-monospace text-xs" rows="10" readonly spellcheck="false" wrap="off"
                  :value="matchQuery"></textarea>
              </div>
              <div v-else class="text-xs text-secondary">
                Click "Show match query" to fetch the aggregation pipeline used for this user.
              </div>
            </div>
          </div>

          <div class="card border shadow-sm mt-3" v-if="registrationMetadataEntries.length">
            <div class="card-body">
              <h6 class="mb-3">Registration metadata</h6>
              <div v-for="entry in registrationMetadataEntries" :key="entry.key" class="mb-2">
                <p class="text-xxs text-secondary text-uppercase mb-1">{{ entry.key }}</p>
                <pre v-if="entry.isMultiline" class="text-sm mb-0 bg-white border rounded p-2"
                  style="white-space: pre-wrap;">{{ entry.display }}</pre>
                <p v-else class="text-sm mb-0">{{ entry.display }}</p>
              </div>
            </div>
          </div>

          <div class="card border shadow-sm mt-3">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h6 class="mb-0">Profile photos</h6>
                <MediaComplianceRuleWidget :photos="photoAtoms" :user-id="user?.id" :user-name="user?.name"
                  @status="handleMediaComplianceStatus" />
              </div>
              <div v-if="photoAtoms.length === 0" class="text-sm text-secondary">
                No photos attached to this profile.
              </div>
              <div v-else class="d-flex flex-wrap gap-3">
                <div v-for="photo in photoAtoms" :key="photo.id || photo.mediaPath" style="width: 140px">
                  <div class="rounded overflow-hidden border" style="width: 140px; height: 140px">
                    <img v-if="getMediaUrl(photo.mediaPath)" :src="getMediaUrl(photo.mediaPath)" alt="profile photo"
                      class="w-100 h-100 object-fit-cover" style="cursor: zoom-in"
                      @click="openMedia(getMediaUrl(photo.mediaPath))" />
                  </div>
                  <button v-if="canEditUser" type="button" class="btn btn-sm mt-1 py-0 px-1 w-100"
                    style="font-size: 0.65rem"
                    :class="isAtomFlaggedInappropriate(photo.id) ? 'btn-danger' : 'btn-outline-danger'"
                    :disabled="inappropriateContentLoadingFor === photo.id"
                    @click="toggleAtomInappropriateContent(photo)">
                    <span v-if="inappropriateContentLoadingFor === photo.id"
                      class="spinner-border spinner-border-sm me-1" role="status"></span>
                    {{ isAtomFlaggedInappropriate(photo.id) ? "🚫 Inappropriate" : "🚩 Mark inappropriate" }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Attributes tab -->
        <div v-else-if="activeTab === 'attributes'">
          <div v-if="groupedAtoms.length === 0" class="text-center py-4 text-secondary">
            No profile attributes found.
          </div>
          <div v-else class="card border shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div v-if="translateError" class="text-danger text-xs">{{ translateError }}</div>
                <div class="position-relative ms-auto">
                  <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="translateLoading"
                    @click="toggleTranslateMenu">
                    <span v-if="translateLoading" class="spinner-border spinner-border-sm me-1"></span>
                    🌐 Translate
                  </button>
                  <div v-if="translateMenuOpen" class="position-absolute bg-white shadow-sm rounded-2 border py-1"
                    style="top: 100%; right: 0; z-index: 20; min-width: 130px;">
                    <button v-for="language in TRANSLATION_LANGUAGES" :key="language.code" type="button"
                      class="dropdown-item text-sm py-1 px-2" @click="translateAttributes(language.code)">
                      {{ language.label }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Attribute
                      </th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="group in groupedAtoms" :key="group.attribute">
                      <tr v-for="(atom, idx) in group.atoms"
                        :key="atom.id || atom.mediaPath || `${group.attribute}-${idx}`">
                        <td class="text-sm fw-bold">
                          {{ group.attribute === "unknown" ? "Unknown attribute" : group.attribute }}
                        </td>
                        <td class="text-sm">
                          <template v-if="atom.mediaPath && atom.mediaPath.includes('voice')">
                            <audio :src="getMediaUrl(atom.mediaPath)" controls></audio>
                          </template>
                          <template v-else-if="atom.mediaPath && atom.mediaPath.includes('photo')">
                            <img :src="getMediaUrl(atom.mediaPath)" alt="profile media" class="img-fluid rounded"
                              style="max-height: 160px; cursor: zoom-in"
                              @click="openMedia(getMediaUrl(atom.mediaPath))" />
                          </template>
                          <template v-else>
                            {{ atom.textValue || atom.intValue || atom.mediaPath || "--" }}
                          </template>
                          <div v-if="translationFor(atom)" class="mt-1 pt-1 text-xs"
                            style="border-top: 1px solid rgba(0, 0, 0, 0.08);">
                            <span class="fw-semibold text-secondary">Translation: </span>{{ translationFor(atom) }}
                          </div>
                          <button v-if="canEditUser && group.attribute === 'bio'" type="button"
                            class="btn btn-xs mt-1 mb-0"
                            :class="isAtomFlaggedInappropriate(atom.id) ? 'btn-danger' : 'btn-outline-danger'"
                            :disabled="inappropriateContentLoadingFor === atom.id"
                            @click="toggleAtomInappropriateContent(atom)">
                            <span v-if="inappropriateContentLoadingFor === atom.id"
                              class="spinner-border spinner-border-sm me-1"></span>
                            {{ isAtomFlaggedInappropriate(atom.id) ? "🚫 Inappropriate" : "🚩 Mark inappropriate" }}
                          </button>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Media tab -->
        <div v-else-if="activeTab === 'media'" class="card">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 card-body">
            <div>
              <h6 class="mb-1">User media</h6>
              <p class="text-xs text-secondary mb-0">
                Uploaded photos and voice messages for this user.
              </p>
            </div>
            <div class="d-flex align-items-center gap-2">
              <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="mediaLoading"
                @click="loadMedia(1)">
                <span v-if="mediaLoading" class="spinner-border spinner-border-sm me-1"></span>
                Reload
              </button>
              <span class="badge bg-light text-dark border">
                {{ mediaPagination.totalItems }} total
              </span>
            </div>
          </div>

          <argon-alert v-if="mediaError" color="danger" class="mb-3">
            {{ mediaError }}
          </argon-alert>

          <div v-if="mediaLoading" class="text-center py-4">
            <div class="spinner-border text-success" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 mb-0 text-sm text-secondary">Loading media...</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Type
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    References
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Display
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Labels
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Created at
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in mediaItems" :key="item.path || item.createdAt?.getTime()">
                  <td class="text-sm fw-semibold text-capitalize">
                    {{ item.type || "--" }}
                  </td>
                  <td>
                    <div class="d-flex flex-wrap gap-1">
                      <template v-for="(reference, idx) in item.references"
                        :key="`${item.path}-${reference.type}-${reference.id}-${idx}`">
                        <router-link v-if="mediaReferenceUrl(reference, item.userId)"
                          :to="mediaReferenceUrl(reference, item.userId)"
                          class="badge badge-sm bg-light text-dark border text-xs fw-light text-decoration-none">
                          {{ mediaReferenceLabel(reference) }}
                        </router-link>
                      </template>
                      <span v-if="!item.references || item.references.length === 0"
                        class="text-xs text-secondary">--</span>
                    </div>
                  </td>
                  <td>
                    <template
                      v-if="getMediaUrl(item.path) && (item.type === 'photo' || (item.path || '').includes('photo'))">
                      <img :src="getMediaUrl(item.path)" alt="user media" class="rounded"
                        style="max-height: 200px; cursor: zoom-in" @click="openMedia(getMediaUrl(item.path))" />
                    </template>
                    <template
                      v-else-if="getMediaUrl(item.path) && (item.type === 'voice' || (item.path || '').includes('voice'))">
                      <audio :src="getMediaUrl(item.path)" controls class="w-100"></audio>
                    </template>
                    <template v-else>
                      <code class="text-xs">{{ item.path || "--" }}</code>
                    </template>
                  </td>
                  <td>
                    <div class="d-flex flex-wrap gap-1">
                      <span v-for="(label, idx) in item.labels" :key="`${item.path}-${label.label}-${idx}`"
                        class="badge badge-sm bg-light text-dark border text-xs fw-light d-inline-flex align-items-center gap-1">
                        {{ label.label }}
                        <span v-if="label.value">: {{ label.value }}</span>
                        <span v-if="label.score != null" class="text-xxs text-secondary">
                          ({{ Number(label.score).toFixed(2) }})
                        </span>
                        <button v-if="canDeleteMedia" type="button" class="btn-close btn-close-dark ms-1 bg-danger"
                          style="font-size: 0.5rem;"
                          @click.stop="handleDeleteMediaLabel(item.path, label.label, label.value ?? null)">
                        </button>
                      </span>
                      <span v-if="!item.labels || item.labels.length === 0" class="text-xs text-secondary">--</span>
                    </div>
                  </td>
                  <td class="text-sm">
                    {{ formatDateTime(item.createdAt) }}
                  </td>
                </tr>
                <tr v-if="!mediaItems.length && !mediaLoading">
                  <td colspan="99" class="text-center text-sm text-secondary py-3">
                    No media found for this user.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="mediaPagination.totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
            <div class="text-sm text-secondary">
              Page {{ mediaPagination.page }} of {{ mediaPagination.totalPages }}
            </div>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-outline-secondary" type="button"
                :disabled="mediaPagination.page <= 1 || mediaLoading" @click="loadMedia(mediaPagination.page - 1)">
                Previous
              </button>
              <button class="btn btn-sm btn-outline-secondary" type="button"
                :disabled="!mediaPagination.hasNext || mediaLoading" @click="loadMedia(mediaPagination.page + 1)">
                Next
              </button>
            </div>
          </div>
        </div>

        <!-- Verification tab -->
        <div v-else-if="activeTab === 'verification'" class="card">
          <div class="card-header pb-0 d-flex align-items-center gap-3 flex-wrap">
            <h6 class="mb-0">{{ user.name || "--" }}</h6>
            <span class="badge" :class="{
              'bg-success': user.status === 'active',
              'bg-warning text-dark': user.status === 'pending',
              'bg-danger': user.status === 'deleted',
              'bg-secondary': !['active', 'pending', 'deleted'].includes(user.status)
            }">{{ user.status || "--" }}</span>
            <span class="text-sm text-secondary">{{ user.gender || "--" }}</span>
            <span class="text-sm text-secondary">{{ ageLabel }}</span>
            <span class="text-sm text-secondary">{{ locationLabel }}</span>
          </div>
          <div class="mb-5 card-body" v-if="verificationAttempts.length > 0">
            <div v-if="photoAtoms.length === 0" class="text-sm text-secondary">
              No photos attached to this profile.
            </div>
            <div v-else class="d-flex flex-wrap gap-3">
              <div v-for="photo in photoAtoms" :key="photo.id || photo.mediaPath" class="rounded overflow-hidden border"
                style="width: 140px; height: 140px">
                <img v-if="getMediaUrl(photo.mediaPath)" :src="getMediaUrl(photo.mediaPath)" alt="profile photo"
                  class="w-100 h-100 object-fit-cover" style="cursor: zoom-in"
                  @click="openMedia(getMediaUrl(photo.mediaPath))" />
              </div>
            </div>
          </div>

          <div v-if="isVerificationLoading" class="text-center py-4">
            <div class="spinner-border text-success" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-sm text-secondary mb-0">Loading verification attempts...</p>
          </div>
          <div v-else-if="verificationErrorMessage" class="text-center py-4 px-3">
            <div class="icon icon-shape bg-gradient-danger shadow text-white border-radius-md mx-auto">
              <i class="ni ni-fat-remove"></i>
            </div>
            <p class="mt-3 text-sm text-dark">{{ verificationErrorMessage }}</p>
            <button class="btn btn-sm btn-success" type="button" @click="loadVerificationAttempts">
              Try again
            </button>
          </div>
          <div v-else-if="verificationAttempts.length === 0" class="text-center py-4 text-secondary">
            No verification attempts found for this user.
          </div>
          <div v-else class="table-responsive">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Status
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Code / Gesture
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Insights
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Photos
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                    Dates
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="attempt in verificationAttempts" :key="attempt.id || attempt.createdAt?.getTime()">
                  <td>
                    <span class="badge bg-light text-dark border text-xxs">
                      {{ formatVerificationAttemptStatus(attempt.status) }}
                    </span>
                    <br>
                    <span class="text-xs text-secondary">
                      {{ attempt.id || "--" }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <span v-if="attempt.verificationCode" class="text-sm font-weight-bold">{{ attempt.verificationCode
                        }}</span>
                      <span v-if="attempt.verificationGesture" class="text-xs text-secondary">{{
                        attempt.verificationGesture
                        }}</span>
                      <span v-if="!attempt.verificationCode && !attempt.verificationGesture" class="text-sm">--</span>
                    </div>
                  </td>
                  <td>
                    <span class="text-xs"
                      style="white-space: pre-wrap; max-width: 180px; display: block; overflow: hidden; text-overflow: ellipsis;"
                      :title="attempt.insights">{{ attempt.insights || "--" }}</span>
                  </td>
                  <td>
                    <div v-if="attempt.verificationMediaPaths.length" class="d-flex flex-wrap gap-2">
                      <template v-for="(path, idx) in attempt.verificationMediaPaths"
                        :key="`${attempt.id}-media-${idx}`">
                        <img v-if="getMediaUrl(path) && path.includes('photo')" :src="getMediaUrl(path)"
                          alt="verification media" class="rounded border" style="max-height: 120px; cursor: zoom-in"
                          @click="openMedia(getMediaUrl(path))" />
                        <audio v-else-if="getMediaUrl(path) && path.includes('voice')" :src="getMediaUrl(path)"
                          controls></audio>
                        <span v-else class="text-sm text-secondary">{{ path }}</span>
                      </template>
                    </div>
                    <div v-else-if="attempt.verificationPhotoPaths.length" class="d-flex flex-wrap gap-2">
                      <button v-for="(path, idx) in attempt.verificationPhotoPaths" :key="`${attempt.id}-${idx}`"
                        type="button" class="btn btn-xs btn-outline-success mb-0"
                        @click="openVerificationPhoto(attempt, idx)">
                        Photo {{ idx + 1 }}
                      </button>
                    </div>
                    <span v-else class="text-sm text-secondary">--</span>
                  </td>
                  <td class="text-center">
                    <span class="text-xs">CreatedAt: {{ formatDateTime(attempt.createdAt) }}</span><br>
                    <span class="text-xs">UpdatedAt: {{ formatDateTime(attempt.updatedAt) }}</span><br>
                    <span class="text-xs">ExpiresAt: {{ formatDateTime(attempt.expiresAt) }}</span>
                  </td>
                  <td class="text-center">
                    <div v-if="attempt.adminNextOptions && attempt.adminNextOptions.length && canEditVerification"
                      class="d-flex flex-wrap gap-2 justify-content-center">
                      <button v-for="status in attempt.adminNextOptions" :key="status" type="button"
                        class="btn btn-sm mb-0"
                        :class="status.includes('approved') || status.includes('verified') ? 'btn-success' : status.includes('rejected') ? 'btn-danger' : 'btn-outline-secondary'"
                        :disabled="updatingAttempt[attempt.id]" @click="updateAttemptStatus(attempt, status)">
                        <span v-if="updatingAttempt[attempt.id] && attemptNextStatus[attempt.id] === status"
                          class="spinner-border spinner-border-sm me-1"></span>
                        {{ formatVerificationAttemptStatus(status) }}
                      </button>
                    </div>
                    <span v-else class="text-sm text-secondary">--</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Purchases tab -->
        <div v-else-if="activeTab === 'purchases'" class="card">
          <div class="card-body">
            <div class="row g-3">
              <div class="col-12">
                <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <div>
                    <h6 class="mb-1">User purchases</h6>
                    <p class="text-xs text-secondary mb-0">Subscription and plan purchases for this user.</p>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <a v-if="revenueCatUrl" :href="revenueCatUrl" class="btn btn-sm btn-outline-info mb-0"
                      target="_blank" rel="noopener noreferrer">
                      RevenueCat
                    </a>
                    <button v-if="canEditPay" class="btn btn-sm btn-outline-success mb-0" type="button"
                      :disabled="purchasesLoading" @click="refreshUserPurchases()">
                      <span v-if="purchasesLoading" class="spinner-border spinner-border-sm me-1"></span>
                      Refresh
                    </button>
                    <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="purchasesLoading"
                      @click="loadPurchases(1)">
                      <span v-if="purchasesLoading" class="spinner-border spinner-border-sm me-1"></span>
                      Reload
                    </button>
                    <span class="badge bg-light text-dark border">
                      {{ purchasesPagination.totalItems }} total
                    </span>
                  </div>
                </div>

                <argon-alert v-if="purchasesError" color="danger" class="mb-3">
                  {{ purchasesError }}
                </argon-alert>

                <div v-if="purchasesLoading" class="text-center py-4">
                  <div class="spinner-border text-success" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-3 mb-0 text-sm text-secondary">Loading purchases...</p>
                </div>

                <div v-else class="table-responsive">
                  <table class="table align-items-center mb-0">
                    <thead>
                      <tr>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Plan</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Type</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Purchase ID
                        </th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Env</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Store</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Revenue</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Period</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Acquired</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="purchase in purchases" :key="purchase.id">
                        <td class="text-sm fw-semibold">{{ purchase.planId || '--' }}</td>
                        <td class="text-sm">{{ purchase.acquisitionType || '--' }}</td>
                        <td class="text-sm">{{ purchase.purchaseId || '--' }}</td>
                        <td>
                          <span class="badge" :class="purchase.status === 'active' ? 'bg-success' : 'bg-secondary'">
                            {{ purchase.status || '--' }}
                          </span>
                        </td>
                        <td class="text-sm">{{ purchase.environment || '--' }}</td>
                        <td class="text-sm">{{ purchase.store || '--' }}</td>
                        <td class="text-sm">
                          <div>
                            Gross: {{ purchase.revenueGross ?? '--' }} |
                            Proceeds: {{ purchase.revenueProceeds ?? '--' }}
                          </div>
                          <div class="text-xs text-secondary">
                            {{ purchase.revenueCurrency || '--' }}
                          </div>
                        </td>
                        <td class="text-sm">
                          <div>{{ purchase.fromDatetime ? formatDateTime(purchase.fromDatetime) : '--' }}</div>
                          <div class="text-xs text-secondary">to {{ purchase.toDatetime ?
                            formatDateTime(purchase.toDatetime)
                            : '--' }}</div>
                          <button v-if="canEditPay" class="btn btn-link text-secondary p-0 text-xs mb-0" type="button"
                            @click="openEditPurchasePeriodModal(purchase)">
                            Edit
                          </button>
                        </td>
                        <td class="text-sm">
                          <div>{{ purchase.acquiredAt ? formatDateTime(purchase.acquiredAt) : '--' }}</div>
                          <div class="text-xs text-secondary">created {{ purchase.createdAt ?
                            formatDateTime(purchase.createdAt) : '--' }}</div>
                        </td>
                        <td class="text-sm">
                          <button v-if="canEditPay && isPurchaseTransferable(purchase)"
                            class="btn btn-link text-secondary p-0 text-xs mb-0" type="button"
                            @click="openTransferPlanModal(purchase)">
                            Transfer to user
                          </button>
                          <span v-else class="text-xs text-secondary">--</span>
                        </td>
                      </tr>
                      <tr v-if="!hasPurchases && !purchasesLoading">
                        <td colspan="99" class="text-center text-sm text-secondary py-3">
                          No purchases found for this user.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="purchasesPagination.totalPages > 1"
                  class="d-flex justify-content-between align-items-center mt-3">
                  <div class="text-sm text-secondary">
                    Page {{ purchasesPagination.page }} of {{ purchasesPagination.totalPages }}
                  </div>
                  <div class="d-flex gap-2">
                    <button class="btn btn-sm btn-outline-secondary" type="button"
                      :disabled="purchasesPagination.page <= 1 || purchasesLoading"
                      @click="loadPurchases(purchasesPagination.page - 1)">
                      Previous
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" type="button"
                      :disabled="!purchasesPagination.hasNext || purchasesLoading"
                      @click="loadPurchases(purchasesPagination.page + 1)">
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <!-- Plan Usage -->
              <div class="col-12 mt-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 class="mb-1">Plan usage</h6>
                    <p class="text-xs text-secondary mb-0">Feature quota consumption across all plans.</p>
                  </div>
                  <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="planUsageLoading"
                    @click="loadPlanUsage()">
                    <span v-if="planUsageLoading" class="spinner-border spinner-border-sm me-1"></span>
                    Reload
                  </button>
                </div>

                <argon-alert v-if="planUsageError" color="danger" class="mb-3">
                  {{ planUsageError }}
                </argon-alert>

                <div v-if="planUsageLoading" class="text-center py-4">
                  <div class="spinner-border text-secondary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-3 mb-0 text-sm text-secondary">Loading usage...</p>
                </div>

                <div v-else-if="planUsage.length === 0" class="text-sm text-secondary py-2">
                  No usage data available.
                </div>

                <div v-else>
                  <div v-for="plan in planUsage" :key="plan.planId" class="mb-4">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <span class="fw-semibold text-sm">{{ plan.planName || plan.planId }}</span>
                      <span class="badge" :class="plan.isActive ? 'bg-success' : 'bg-secondary'">
                        {{ plan.isActive ? 'active' : 'inactive' }}
                      </span>
                      <span class="text-xs text-secondary">{{ plan.acquisitionType }}</span>
                    </div>
                    <div v-if="plan.features && plan.features.length > 0" class="table-responsive">
                      <table class="table table-sm align-items-center mb-0">
                        <thead>
                          <tr>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Feature</th>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Limit</th>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Used</th>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Adjusted
                            </th>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Remaining
                            </th>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Last used
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="feature in plan.features" :key="feature.id">
                            <td class="text-sm">{{ feature.payableFeature }}</td>
                            <td class="text-sm">{{ feature.windowLimit === -1 ? '∞' : feature.windowLimit }}</td>
                            <td class="text-sm">
                              {{ feature.usageCount ?? '--' }}
                              <button v-if="canEditPay" class="btn btn-link text-secondary p-0 text-xs mb-0 ms-1"
                                type="button" @click="openEditUsageModal(plan, feature)">
                                Edit
                              </button>
                            </td>
                            <td class="text-sm">{{ feature.adjustedLastUsedCount != null ?
                              feature.adjustedLastUsedCount.toFixed(2) : '--' }}</td>
                            <td class="text-sm">
                              <span v-if="feature.remainingQuota === -1">∞</span>
                              <span v-else>{{ feature.remainingQuota != null ? feature.remainingQuota.toFixed(2) : '--'
                                }}</span>
                            </td>
                            <td class="text-sm text-secondary">{{ feature.lastUsedAt ?
                              formatDateTime(feature.lastUsedAt) : '--'
                              }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else class="text-xs text-secondary">No features defined for this plan.</div>
                  </div>
                </div>
              </div>

              <!-- Wallet -->
              <div class="col-12 mt-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 class="mb-1">Wallet</h6>
                    <p class="text-xs text-secondary mb-0">Balance and transaction history.</p>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <a v-if="walletTopUpUrl" :href="walletTopUpUrl" target="_blank" rel="noopener"
                      class="btn btn-sm btn-outline-success mb-0">
                      Wallet
                    </a>
                    <button v-if="canEditPay" class="btn btn-sm btn-outline-primary mb-0" type="button"
                      @click="openAddTransactionModal()">
                      Add transaction
                    </button>
                    <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="walletLoading"
                      @click="loadWallet()">
                      <span v-if="walletLoading" class="spinner-border spinner-border-sm me-1"></span>
                      Reload
                    </button>
                  </div>
                </div>

                <argon-alert v-if="walletError" color="danger" class="mb-3">
                  {{ walletError }}
                </argon-alert>

                <div v-if="walletLoading" class="text-center py-4">
                  <div class="spinner-border text-secondary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-3 mb-0 text-sm text-secondary">Loading wallet...</p>
                </div>

                <div v-else-if="wallet">
                  <div class="mb-3">
                    <span class="fw-semibold text-sm">Balance: </span>
                    <span class="text-sm">${{ wallet.balance?.amount?.toFixed(2) ?? '--' }} {{
                      wallet.balance?.currencyCode
                      }}</span>
                  </div>
                  <div v-if="wallet.transactions?.length > 0" class="table-responsive">
                    <table class="table table-sm align-items-center mb-0">
                      <thead>
                        <tr>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Channel</th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Amount</th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Description
                          </th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Transaction ID
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="tx in wallet.transactions" :key="tx.transactionId">
                          <td class="text-sm text-secondary">{{ tx.createdAt ? formatDateTime(new Date(tx.createdAt)) :
                            '--' }}
                          </td>
                          <td class="text-sm">{{ tx.channel || '--' }}</td>
                          <td class="text-sm fw-semibold" :class="tx.cents >= 0 ? 'text-success' : 'text-danger'">
                            {{ tx.cents >= 0 ? '+' : '' }}${{ tx.amount?.toFixed(2) ?? '--' }}
                          </td>
                          <td class="text-sm text-secondary">{{ tx.description || '--' }}</td>
                          <td class="text-sm text-secondary font-monospace" style="font-size:11px">{{ tx.transactionId
                            || '--'
                            }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-sm text-secondary py-2">No transactions found.</div>
                </div>

                <div v-else class="text-sm text-secondary py-2">Wallet data not loaded.</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sessions tab -->
        <div v-else-if="activeTab === 'sessions'" class="card">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 card-body">
            <div>
              <h6 class="mb-1">Sessions</h6>
              <p class="text-xs text-secondary mb-0">
                Devices and login sessions for this user.
              </p>
            </div>
            <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="sessionsLoading"
              @click="loadSessions">
              <span v-if="sessionsLoading" class="spinner-border spinner-border-sm me-1"></span>
              Reload
            </button>
          </div>

          <argon-alert v-if="sessionsError" color="danger" class="mb-3">
            {{ sessionsError }}
          </argon-alert>

          <div v-if="sessionsLoading" class="text-center py-4">
            <div class="spinner-border text-success" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 mb-0 text-sm text-secondary">Loading sessions...</p>
          </div>

          <div v-else-if="sessions.length === 0" class="text-center py-4 text-secondary card-body">
            No sessions recorded for this user.
          </div>
          <div v-else class="table-responsive card-body">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Device ID
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Platform
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    App version
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Language
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Timezone
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                    Login count
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                    FCM errors
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                    Last login
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                    Logged out
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                    Created
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    IPs
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Countries
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    SIM countries
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Locales
                  </th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="session in sessions" :key="session.id || session.deviceId">
                  <td><span class="text-sm">{{ session.deviceId || "--" }}</span></td>
                  <td><span class="text-sm">{{ session.platform || "--" }}</span></td>
                  <td><span class="text-sm">{{ session.appVersion || "--" }}</span></td>
                  <td><span class="text-sm">{{ session.language || "--" }}</span></td>
                  <td><span class="text-sm">{{ session.timezone || "--" }}</span></td>
                  <td class="text-center">
                    <span class="text-sm">{{ session.loginCount ?? "--" }}</span>
                  </td>
                  <td class="text-center">
                    <span class="text-sm">{{ session.fcmTokenErrorCount ?? "--" }}</span>
                  </td>
                  <td class="text-center">
                    <span class="text-sm">{{ formatDateTime(session.lastLoginAt) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="text-sm">{{ formatDateTime(session.loggedOutAt) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="text-sm">{{ formatDateTime(session.createdAt) }}</span>
                  </td>
                  <td>
                    <span class="text-xs text-wrap">{{ formatList(session.ips, " | ") }}</span>
                  </td>
                  <td>
                    <span class="text-sm">{{ formatList(session.countries, " | ") }}</span>
                  </td>
                  <td>
                    <span class="text-sm">{{ formatList(session.simCountries, " | ") }}</span>
                  </td>
                  <td>
                    <span class="text-sm">{{ formatList(session.systemLocales, " | ") }}</span>
                  </td>
                  <td>
                    <button class="btn btn-xs btn-outline-danger mb-0" type="button" @click="getSessionJwt(session)"
                      :disabled="!session.id || session.loggedOutAt || sessionJwtLoading[session.id]"
                      :title="session.loggedOutAt ? 'Session is logged out' : (!session.id ? 'Session id missing' : '')">
                      <span v-if="sessionJwtLoading[session.id]" class="spinner-border spinner-border-sm me-1"
                        role="status" aria-hidden="true"></span>
                      Get JWT
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showDeleteModal" class="modal fade show d-block" tabindex="-1" style="background: rgba(0, 0, 0, 0.5);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h6 class="modal-title text-danger">Delete user</h6>
          <button type="button" class="btn-close" aria-label="Close" @click="closeDeleteModal"
            :disabled="isDeleting"></button>
        </div>
        <div class="modal-body">
          <p class="text-sm">
            This action will permanently delete this user and cannot be undone.
            Type <strong>DELETE</strong> to confirm.
          </p>
          <input type="text" class="form-control" placeholder="Type DELETE to confirm" v-model="deleteConfirmation"
            :disabled="isDeleting" @keyup.enter="confirmDelete" />
          <input type="text" class="form-control mt-2" placeholder="Reason (required)" v-model="deleteReason"
            :disabled="isDeleting" @keyup.enter="confirmDelete" />
          <p v-if="deleteError" class="text-danger text-xs mt-2 mb-0">
            {{ deleteError }}
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="closeDeleteModal" :disabled="isDeleting">
            Cancel
          </button>
          <button type="button" class="btn btn-danger" @click="confirmDelete" :disabled="isDeleting">
            <span v-if="isDeleting" class="spinner-border spinner-border-sm me-2"></span>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showAddTransactionModal" class="modal fade show d-block" tabindex="-1"
    style="background: rgba(0, 0, 0, 0.5);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h6 class="modal-title">Add wallet transaction</h6>
          <button type="button" class="btn-close" aria-label="Close" @click="closeAddTransactionModal"
            :disabled="isAddingTransaction"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label text-sm">Type</label>
            <select class="form-control" v-model="newTransaction.type" :disabled="isAddingTransaction">
              <option value="credit">Credit (add funds)</option>
              <option value="debit">Debit (remove funds)</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label text-sm">Amount</label>
            <input type="number" min="0.01" step="0.01" class="form-control" placeholder="0.00"
              v-model="newTransaction.amount" :disabled="isAddingTransaction" />
          </div>
          <div class="mb-0">
            <label class="form-label text-sm">Description</label>
            <textarea class="form-control" rows="2" placeholder="Reason for this transaction"
              v-model="newTransaction.description" :disabled="isAddingTransaction"></textarea>
          </div>
          <p v-if="addTransactionError" class="text-danger text-xs mt-2 mb-0">
            {{ addTransactionError }}
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="closeAddTransactionModal"
            :disabled="isAddingTransaction">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="submitAddTransaction" :disabled="isAddingTransaction">
            <span v-if="isAddingTransaction" class="spinner-border spinner-border-sm me-2"></span>
            Add transaction
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showEditPurchasePeriodModal" class="modal fade show d-block" tabindex="-1"
    style="background: rgba(0, 0, 0, 0.5);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h6 class="modal-title">Edit purchase period</h6>
          <button type="button" class="btn-close" aria-label="Close" @click="closeEditPurchasePeriodModal"
            :disabled="isSavingPurchasePeriod"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label text-sm">From</label>
            <div class="input-group">
              <input type="datetime-local" class="form-control" v-model="editingPurchase.fromDatetime"
                :disabled="isSavingPurchasePeriod" />
              <button type="button" class="btn btn-outline-secondary mb-0"
                @click="setPurchasePeriodFieldToNow('fromDatetime')" :disabled="isSavingPurchasePeriod">
                Now
              </button>
            </div>
          </div>
          <div class="mb-0">
            <label class="form-label text-sm">To</label>
            <div class="input-group">
              <input type="datetime-local" class="form-control" v-model="editingPurchase.toDatetime"
                :disabled="isSavingPurchasePeriod" />
              <button type="button" class="btn btn-outline-secondary mb-0"
                @click="setPurchasePeriodFieldToNow('toDatetime')" :disabled="isSavingPurchasePeriod">
                Now
              </button>
            </div>
          </div>
          <p v-if="editPurchasePeriodError" class="text-danger text-xs mt-2 mb-0">
            {{ editPurchasePeriodError }}
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="closeEditPurchasePeriodModal"
            :disabled="isSavingPurchasePeriod">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="submitEditPurchasePeriod"
            :disabled="isSavingPurchasePeriod">
            <span v-if="isSavingPurchasePeriod" class="spinner-border spinner-border-sm me-2"></span>
            Save
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showEditUsageModal" class="modal fade show d-block" tabindex="-1"
    style="background: rgba(0, 0, 0, 0.5);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h6 class="modal-title">Edit feature usage</h6>
          <button type="button" class="btn-close" aria-label="Close" @click="closeEditUsageModal"
            :disabled="isSavingUsage"></button>
        </div>
        <div class="modal-body">
          <p class="text-xs text-secondary">
            Plan <strong>{{ editingUsage.planName || '--' }}</strong>, feature
            <strong>{{ editingUsage.payableFeature || '--' }}</strong>.
          </p>
          <div class="mb-0">
            <label class="form-label text-sm">Used</label>
            <input type="number" min="0" step="1" class="form-control" v-model="editingUsage.usageCount"
              :disabled="isSavingUsage" />
          </div>
          <p v-if="editUsageError" class="text-danger text-xs mt-2 mb-0">
            {{ editUsageError }}
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="closeEditUsageModal"
            :disabled="isSavingUsage">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="submitEditUsage" :disabled="isSavingUsage">
            <span v-if="isSavingUsage" class="spinner-border spinner-border-sm me-2"></span>
            Save
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showTransferPlanModal" class="modal fade show d-block" tabindex="-1"
    style="background: rgba(0, 0, 0, 0.5);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h6 class="modal-title">Transfer plan to another user</h6>
          <button type="button" class="btn-close" aria-label="Close" @click="closeTransferPlanModal"
            :disabled="isTransferringPlan"></button>
        </div>
        <div class="modal-body">
          <p class="text-xs text-secondary">
            This ends plan <strong>{{ transferringPurchase.planId || '--' }}</strong> for this user now, and grants
            the remaining time to the destination user.
          </p>
          <div class="mb-2">
            <label class="form-label text-sm">Destination user ID</label>
            <div class="input-group">
              <input type="text" class="form-control" placeholder="Destination user ID"
                v-model="transferDestinationUserId" :disabled="isTransferringPlan || isLookingUpTransferUser"
                @keyup.enter="lookupTransferDestinationUser" />
              <button type="button" class="btn btn-outline-secondary mb-0" @click="lookupTransferDestinationUser"
                :disabled="isTransferringPlan || isLookingUpTransferUser">
                <span v-if="isLookingUpTransferUser" class="spinner-border spinner-border-sm"></span>
                <span v-else>Look up</span>
              </button>
            </div>
          </div>
          <p v-if="transferLookupError" class="text-danger text-xs mt-2 mb-0">
            {{ transferLookupError }}
          </p>
          <div v-if="transferResolvedUser" class="alert alert-success text-xs mt-3 mb-0 py-2">
            Transferring to <strong>{{ transferResolvedUser.name || transferResolvedUser.id }}</strong>
            ({{ transferResolvedUser.id }})
          </div>
          <p v-if="transferPlanError" class="text-danger text-xs mt-3 mb-0">
            {{ transferPlanError }}
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="closeTransferPlanModal"
            :disabled="isTransferringPlan">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="submitTransferPlan"
            :disabled="isTransferringPlan || !transferResolvedUser?.id">
            <span v-if="isTransferringPlan" class="spinner-border spinner-border-sm me-2"></span>
            Confirm transfer
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showMediaModal && currentMediaUrl" class="modal fade show d-block" tabindex="-1"
    style="background: rgba(0, 0, 0, 0.75);" @click.self="closeMedia">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content bg-transparent border-0 shadow-none">
        <div class="modal-body text-center p-0">
          <img :src="currentMediaUrl" alt="media preview" class="img-fluid rounded" />
        </div>
        <div class="modal-footer justify-content-center border-0">
          <button type="button" class="btn btn-light" @click="closeMedia">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
